import { FieldReplacement, ReplacementTemplate } from '@ekkogmbh/apisdk';
import React, { useCallback, useMemo, useState } from 'react';
import { createContextHardened } from 'src/Common/Helper/ContextHelper';

interface Context {
  /** Compartment Fields Templates object */
  fieldTemplates: Record<string, ReplacementTemplate>;
  /** Field Templates in Array format, as required by some applications */
  replacements: FieldReplacement[];
  fieldKeys: string[];
  fromReplacements: (replacements: FieldReplacement[]) => void;
  setFieldTemplates: (templates: Record<string, ReplacementTemplate>) => void;
  setField: (key: string, template: ReplacementTemplate) => void;
  removeField: (key: string) => void;
}
const [CompartmentGeneratorContext, useCompartmentGenerator] = createContextHardened<Context>('Compartment Generator');
export default useCompartmentGenerator;

interface Props {
  children: React.ReactNode;
}
export const CompartmentGeneratorProvider = ({ children }: Props): React.JSX.Element => {
  const [fields, setFields] = useState<Record<string, ReplacementTemplate>>({});
  const fieldKeys = useMemo(() => Object.keys(fields), [fields]);

  const replacements: FieldReplacement[] = useMemo(
    () => Object.entries(fields).map(([key, { pattern, replacement }]) => ({ key, pattern, replacement })),
    [fields],
  );

  const fromReplacements = useCallback((replacements: FieldReplacement[]) => {
    setFields(
      replacements.reduce((carry: Record<string, ReplacementTemplate>, rpl) => {
        const { key, replacement, pattern } = rpl;
        carry[key] = { replacement, pattern };
        return carry;
      }, {}),
    );
  }, []);

  const setField = useCallback(
    (key: string, template: ReplacementTemplate) => {
      setFields({
        ...fields,
        [key]: template,
      });
    },
    [fields],
  );

  const removeField = useCallback(
    (key: string) => {
      if (fieldKeys.includes(key)) {
        delete fields[key];
        setFields({ ...fields });
      }
    },
    [fields, fieldKeys],
  );

  return (
    <CompartmentGeneratorContext.Provider
      value={{
        fieldTemplates: fields,
        replacements,
        fieldKeys,
        fromReplacements,
        setField,
        setFieldTemplates: setFields,
        removeField,
      }}
    >
      {children}
    </CompartmentGeneratorContext.Provider>
  );
};
